import { Brand, createBranded } from '@deltasierra/type-utilities';
import {
    FullAuditable,
    HasId,
    FullAuditableKeys,
    Omit,
    CurrencyNumber,
    CurrencyCode,
    PathBuilder,
    ApiRoute,
    HttpMethod,
} from '../common';
import { BuilderTemplateFormatId } from '../builderTemplate';
import { AgencyApiParams, AgencyId } from '../agency';
import { QuantityOptionCrudDto, PrintProviderSKUQuantityOptionDto } from './PrintProviderSKUQuantityOption';
import { PrintProviderSKUGroupId } from './PrintProviderSKUGroup';

export type PrintProviderSKUId = Brand<number, 'PrintProviderSKUId'>;
export const PrintProviderSKUId = createBranded<PrintProviderSKUId>();

export interface PrintProviderSKU extends HasId<PrintProviderSKUId>, FullAuditable {
    printProviderSKUGroupId: PrintProviderSKUGroupId;
    sku: string;
    skuName: string;
    priceAmountInCents: CurrencyNumber | null;
    priceCurrency: CurrencyCode;
    linearPricing: boolean;
    exclusive: boolean;
}

export interface PrintProviderSKUWithNonLinearPricing extends PrintProviderSKU {
    priceAmountInCents: null;
    linearPricing: false;
}

export interface PrintProviderSKUWithLinearPricing extends PrintProviderSKU {
    priceAmountInCents: number;
    linearPricing: true;
}

export type PrintProviderSKUWithEitherPricing =
    | PrintProviderSKUWithLinearPricing
    | PrintProviderSKUWithNonLinearPricing;

export function isLinearPricingSku(
    printProviderSku: PrintProviderSKUWithEitherPricing,
): printProviderSku is PrintProviderSKUWithLinearPricing {
    return printProviderSku.linearPricing;
}

export type GetAllSKUsForSKUGroupDto = Omit<PrintProviderSKU, FullAuditableKeys>;

export interface ICreateOrUpdatePrintProviderSKUBody {
    printProviderSKUGroupId: PrintProviderSKUGroupId; // TODO: this should probably be in the URL path parameter
    sku: string;
    skuName: string;
    priceAmountInCents: CurrencyNumber | null;
    priceCurrency: CurrencyCode;
    exclusive: boolean;
    printProviderSKUQuantityOptions: QuantityOptionCrudDto[];
    builderTemplateFormatIds: BuilderTemplateFormatId[];
}

export interface GetUpdateAgencySKUResponse extends PrintProviderSKU {
    printProviderSKUQuantityOptions: PrintProviderSKUQuantityOptionDto[];
}

export interface GetMappedSKUFormatOptionsResponse {
    printProviderSKUId: PrintProviderSKUId;
    builderTemplateFormatId: BuilderTemplateFormatId;
}

export type PrintProviderSKUDto = Omit<PrintProviderSKU, FullAuditableKeys> & {
    printProviderSKUQuantityOptions: PrintProviderSKUQuantityOptionDto[];
};

interface AgencySKUParams {
    agencyId: AgencyId;
    printProviderSKUId: PrintProviderSKUId;
}

function SKUS_COLLECTION_API_ROOT<T extends AgencyApiParams>(path: PathBuilder<T>) {
    return path.literal('agencies').param('agencyId').literal('printProviderSKUs');
}

function SKU_API_ROOT<T extends AgencySKUParams>(path: PathBuilder<T>) {
    return SKUS_COLLECTION_API_ROOT(path).param('printProviderSKUId');
}

export const PrintProviderSKUsApi = {
    createSKU: new ApiRoute<PrintProviderSKU, AgencyApiParams, ICreateOrUpdatePrintProviderSKUBody, void>(
        HttpMethod.Post,
        SKUS_COLLECTION_API_ROOT,
    ),
    deleteSKU: new ApiRoute<void, AgencySKUParams, void, void>(HttpMethod.Delete, SKU_API_ROOT),
    getSKU: new ApiRoute<PrintProviderSKU, AgencySKUParams, void, void>(HttpMethod.Get, SKU_API_ROOT),
    getSKUQuantityOptions: new ApiRoute<PrintProviderSKUQuantityOptionDto[], AgencySKUParams, void, void>(
        HttpMethod.Get,
        path => SKU_API_ROOT(path).literal('printProviderSKUQuantityOptions'),
    ),
    updateSKU: new ApiRoute<GetUpdateAgencySKUResponse, AgencySKUParams, ICreateOrUpdatePrintProviderSKUBody, void>(
        HttpMethod.Patch,
        SKU_API_ROOT,
    ),
};
